import React, {useState} from 'react';
import {Organisation, OrganisationData} from "types/Organisation";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Field from "components/Form/Field";
import FieldHeader from "components/Form/FieldHeader";
import Uploader from "components/Form/Uploader";
import FormControls from "components/FormControls";
import {PhotographIcon} from "@heroicons/react/outline";
import Label from "components/Form/Label";
import Select from "components/Form/Select";
import ngage from "services/ngage";
import {Option} from "components/Form/Select/Select";
import IconName from "types/IconName";
import {Member} from "types/Member";
import Toggle from "../../../components/Form/Toggle";
import LinkEditor from "../../../components/Form/LinkEditor";
import {AddressFieldSet} from "../../../components/Form/Form/Fieldset";
import * as Yup from "yup";
import {urlTest} from "../../../services/validation-schemas";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {TextAreaField} from "../../../components/Form/Form";

// Types ----

type OrganisationFormProps = {
  organisation?: Organisation | null
  onSubmit: (values: OrganisationData, helpers: FormikHelpers<OrganisationData>) => any
}

// Helpers ----

const memberOption = (member: Member): Option => ({
  id: member.id,
  name: member.name,
  description: member.email,
  icon: IconName.USER
})

function OrganisationForm({onSubmit, organisation}: OrganisationFormProps) {
  const [hasLink, setHasLink] = useState<boolean>(!!organisation?.link);
  const [selectedMembers, setSelectedMembers] = useState<Array<Option>>(organisation?.members.map(memberOption) || []);

  function toggleLink(enabled: boolean, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('link', enabled ? {text: '', url: ''} : null)
    setHasLink(enabled)
  }

  async function userSource(query: string) {
    return (await ngage.users.filtered({search: query || null}))?.data.map(memberOption) || []
  }

  function handleSelectMembers(members: Array<Option>, formik: FormikProps<OrganisationData>) {
    void formik.setFieldValue('member_ids', members.map(member => member.id))
    setSelectedMembers(members)
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(250, 'Maximum of 250 characters'),
    about: Yup.string().required("This field is required"),
    link: Yup.object().shape({
      text: Yup.string().required('This field is required').max(100, 'Maximum 100 characters'),
      url: Yup.string()
        .required('This field is required')
        .max(250, 'Maximum 250 characters')
        .test('valid-url', urlTest),
    }).nullable(),
    email: Yup.string().email('Email address is not valid').max(250, 'Maximum of 250 characters'),
    phone: Yup.string().max(15, 'Maximum of 15 characters'),
    address: Yup.object().shape({
      line1: Yup.string().required('This field is required').max(250, 'Maximum of 250 characters'),
      line2: Yup.string().max(250, 'Maximum of 250 characters'),
      line3: Yup.string().max(250, 'Maximum of 250 characters'),
      city: Yup.string().required('This field is required').max(100, 'Maximum of 100 characters'),
      postcode: Yup.string().required('This field is required').max(20, 'Maximum of 20 characters'),
      country: Yup.string().required('This field is required'),
    }),
  })

  const initialValues: OrganisationData = {
    name: organisation?.name || '',
    about: organisation?.about || '',
    email: organisation?.email || '',
    phone: organisation?.phone || '',
    address: {
      line1: organisation?.address?.line1 || '',
      line2: organisation?.address?.line2 || '',
      line3: organisation?.address?.line3 || '',
      city: organisation?.address?.city || '',
      postcode: organisation?.address?.postcode || '',
      country: organisation?.address?.country || '',
    },
    logo_id: organisation?.logo?.id || null,
    background_id: organisation?.background?.id || null,
    link: organisation?.link ? {text: organisation.link.text, url: organisation.link.url} : null,
    member_ids: organisation?.members.map(member => member.id) || [],
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Key Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        The organisation's identity and general information
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      {/* Name */}
                      <div className="sm:col-span-4 xl:col-span-3">
                        <Field
                          label="Name *"
                          type="text"
                          name="name"
                          // maxLength={255}
                          autoFocus
                        />
                      </div>
  
                      {/* About */}
                      <div className="sm:col-span-6">
                        <TextAreaField
                          label="About"
                          hint="Optional. 20,000 chars max."
                          as="textarea"
                          rows={6}
                          name="about"
                          maxChars={20000}
                        />
                      </div>
  
                      {/* Logo */}
                      <div className="sm:col-span-6">
                        <FieldHeader label="Logo"/>
                        <Uploader
                          id={'logo_id'}
                          name={'logo_id'}
                          icon={PhotographIcon}
                          files={organisation?.logo}
                          type={Uploader.types.IMAGE}
                          maxFiles={1}
                          onChange={files => formik.setFieldValue('logo_id', files[0]?.id ?? null)}
                        />
                      </div>
  
                      {/* Background */}
                      <div className="sm:col-span-6">
                        <FieldHeader label="Background Image"/>
                        <Uploader
                          id={'background_id'}
                          name={'background_id'}
                          icon={PhotographIcon}
                          files={organisation?.background}
                          type={Uploader.types.IMAGE}
                          maxFiles={1}
                          onChange={files => formik.setFieldValue('background_id', files[0]?.id ?? null)}
                        />
                      </div>
  
                      {/* Link */}
                      <div className="sm:col-span-6">
                        <Toggle
                          label="Link"
                          checked={hasLink}
                          onChange={checked => toggleLink(checked, formik)}
                        />
  
                        {hasLink &&
                          <div className="mt-2">
                            <LinkEditor name="link" />
                          </div>
                        }
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Contact Details
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Details that can be used to get in touch with staff at the organisation
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      {/* Email Address */}
                      <div className="sm:col-span-3">
                        <Label htmlFor="email">Email Address</Label>
                        <div className="mt-1">
                          <Field
                            type="email"
                            name="email"
                            id="email"
                            autoComplete="off"
                            maxLength="255"
                          />
                        </div>
                      </div>
  
                      {/* Phone Number */}
                      <div className="sm:col-span-3">
                        <Label htmlFor="phone">Phone Number</Label>
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="phone"
                            id="phone"
                            autoComplete="off"
                            maxLength="255"
                          />
                        </div>
                      </div>
  
                      {/* Address */}
                      <div className="sm:col-span-6">
                        <AddressFieldSet name="address" label="Street Address"/>
                      </div>
                    </div>
                  </div>
  
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Members
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Users that are members of this organisation
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
  
                      {/* Members */}
                      <div className="sm:col-span-6">
                        <Select
                          label="Members"
                          hint="Type to search"
                          source={userSource}
                          selected={selectedMembers}
                          onChange={selected => handleSelectMembers(selected, formik)}
                          multiple
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
  
              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default OrganisationForm;